import {ActivityFilter} from './settings.service';
import {ExpressionFilterSpecification} from 'maplibre-gl';
import {ShiftModel} from '../shared/models/shift.model';

export class MapFilters {
    static getInactiveShiftsFilter(
        gpsTracksMode: boolean,
        activeShiftIds: number[],
        vehicleIds: number[],
        hours: number,
        cutoff: Date,
        flagsFilter: any[],
        showRecent: boolean,
        tileSource = true,
    ) {
        const filter: any[] = ['all'];
        if (!showRecent || !gpsTracksMode) {
            // hiding all if recent are not allowed
            // OR different theme from GPS Tracks, filtering all out
            filter.push(['boolean', false]);
            return filter;
        }
        if (gpsTracksMode) {
            // GPS Tracks theme, filtering all inactive shifts
            if (activeShiftIds) {
                // filter only inactive shifts
                filter.push(['!', ['in', ['get', 'sessionkey'], ['literal', activeShiftIds]]]);
            }
            // only selected vehicles
            if (vehicleIds) {
              filter.push(['in', ['get', 'locationsourceid'], ['literal', vehicleIds]]);
            }
          // no highlighted shift exception here, inactive shift cannot be highlighted
        }

        if (hours) {
            // apply hour filter from track bar
            const minTime = new Date().getTime() / 1000 - hours * 3600;
            filter.push(['>=', ['get', tileSource ? 'startunixtime' : 'start_unixtime'], minTime]);
        }

        if (cutoff) {
          const cutoffTime = new Date(cutoff).getTime() / 1000;
          filter.push(['>=', ['get', tileSource ? 'startunixtime' : 'start_unixtime'], cutoffTime]);
        }

        // apply flagsFilter
        if (flagsFilter.length > 0) {
            filter.push(flagsFilter);
        }

        return filter;
    }

    static getActiveShiftsFilter(
        gpsTracksMode: boolean,
        activeShiftIds: number[],
        vehicleIds: number[],
        hours: number,
        cutoff: Date,
        flagsFilter: any[],
        highlightedShift: ShiftModel,
        tileSource = true,
    ) {
        const filter: any[] = ['all'];
        if (gpsTracksMode) {
            // GPS Tracks theme, filtering all active shifts
            if (activeShiftIds) {
              // filter only active shifts
              filter.push(['in', ['get', 'sessionkey'], ['literal', activeShiftIds]]);
            }
            if (vehicleIds) {
              // only selected vehicles
              filter.push(['in', ['get', 'locationsourceid'], ['literal', vehicleIds]]);
            }

            if (hours) {
                // apply hour filter from track bar
                const minTime = new Date().getTime() / 1000 - hours * 3600;
                filter.push(['>=', ['get', tileSource ? 'startunixtime' : 'start_unixtime'], minTime]);
            }

            if (cutoff) {
              const cutoffTime = new Date(cutoff).getTime() / 1000;
              filter.push(['>=', ['get', tileSource ? 'startunixtime' : 'start_unixtime'], cutoffTime]);
            }

            // apply flagsFilter
            if (flagsFilter.length > 0) {
                filter.push(flagsFilter);
            }
        } else {
            // different theme from GPS Tracks

            // no selected vehicle
            if (!!highlightedShift) {
              // shift highlighted, filtering the shift
              filter.push(['==', ['get', 'sessionkey'], highlightedShift?.id]);
            } else {
              // no highlighted shift, filtering all out
              filter.push(['boolean', false]);
              return filter;
            }
        }

        return filter;
    }

    static getTrackFilter(trackFilter: ActivityFilter): any[] {
        const filter: any[] = ['any'];
        const bitNumbers = ActivityFilter.toBitNumberArray(trackFilter);
        if (bitNumbers && bitNumbers.length > 0) {
            for (const bitNumber of bitNumbers) {
                filter.push(['>=', ['%', ['get', 'flags'], Math.pow(2, bitNumber + 1)], Math.pow(2, bitNumber)]);
            }
        } else {
            filter.push(['boolean', true]);
        }
        return filter;
    }

    static getShiftGeoJsonFilter(
        trackFilter: ActivityFilter,
        positionFilterFrom?: number,
        positionFilterTo?: number,
        shiftIds?: number[], // live map parameter only
        vehicleId?: number, // live map parameter only
    ) {
        const filter: any[] = ['all'];
        if (!!positionFilterFrom) {
            filter.push(['>=', ['get', 'time_start'], positionFilterFrom]);
        }
        if (!!positionFilterTo) {
            filter.push(['<=', ['get', 'time_end'], positionFilterTo]);
        }
        if (!!shiftIds) {
            filter.push(['in', ['get', 'sessionkey'], ['literal', shiftIds]]);
        }
        if (!!vehicleId) {
          filter.push(['==', ['get', 'locationsourceid'], +vehicleId]);
        }

        const bitNumbers = ActivityFilter.toBitNumberArray(trackFilter);
        if (bitNumbers && bitNumbers.length > 0) {
            const trackTypeFilter: any[] = ['any'];
            for (const bitNumber of bitNumbers) {
                trackTypeFilter.push(['>=', ['%', ['get', 'flags'], Math.pow(2, bitNumber + 1)], Math.pow(2, bitNumber)]);
            }
            filter.push(trackTypeFilter);
        }

        return filter;
    }

    /**
     * -2 equals none
     * -1 equals all
     * any number filters the route ids
     */
    static getRoutesFilter(
        configId: number,
        routeLayerId: number,
        configIdFilter: number,
        routeIdFilter: number[],
    ): ExpressionFilterSpecification {
        const filter: ExpressionFilterSpecification = ['all'];
        // filter only route id layer
        filter.push(['==', ['get', 'routeid'], routeLayerId]);
        filter.push(['==', ['get', 'configid'], configId]);
        if (configIdFilter) {
            filter.push(['==', ['get', 'configid'], configIdFilter]);
        }
        if (routeIdFilter && routeIdFilter.length > 0 &&
            (routeIdFilter.length > 1 || routeIdFilter[0] >= 0)
        ) {
            // selected routes
            filter.push(['in', ['get', 'routeid'], ['literal', routeIdFilter]]);
        } else if (routeIdFilter && routeIdFilter.length === 1 && routeIdFilter[0] === -2) {
            // none
            filter.push(['boolean', false]);
        } // else no filter = show all

        return filter;
    }

    static getRouteConfigFilter(
      configId: number,
      configIdFilter: number,
      routeIdFilter: number[],
    ): ExpressionFilterSpecification {
        const filter: ExpressionFilterSpecification = ['all'];
        filter.push(['==', ['get', 'configid'], configId]);
        if (configIdFilter) {
            filter.push(['==', ['get', 'configid'], configIdFilter]);
        }
        if (routeIdFilter && routeIdFilter.length > 0 &&
          (routeIdFilter.length > 1 || routeIdFilter[0] >= 0)
        ) {
            // selected routes
            filter.push(['in', ['get', 'routeid'], ['literal', routeIdFilter]]);
        } else if (routeIdFilter && routeIdFilter.length === 1 && routeIdFilter[0] === -2) {
            // none
            filter.push(['boolean', false]);
        } // else no filter = show all

        return filter;
    }
}

